import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { apiFetcher } from '@/data/fetcher'
import { VariantAxisFormModal } from '@/forms/VariantAxisForm/VariantAxisFormModal'
import { VariantAxis } from '@/interfaces/catalog/VariantAxis'
import { Settings } from '@/interfaces/Settings'

interface VariantAxisChecklistProps {
  value: string[]
  onChange: (axisIds: string[]) => void
}

export const VariantAxisChecklist = ({ value, onChange }: VariantAxisChecklistProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog',
  })
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const { data: response, mutate } = useSWR<{ data?: VariantAxis[]; total?: number }>(
    '/api/v2/variant-axes',
    apiFetcher,
  )
  const axes = response?.data || []
  const { data: settings } = useSWR<Settings>('/api/v2/settings', apiFetcher)
  const maxAxes = settings?.maxVariantAxes
  const isFull = maxAxes !== undefined && value.length >= maxAxes

  const toggle = (axisId: string, checked: boolean) =>
    onChange(checked ? [...value, axisId] : value.filter((id) => id !== axisId))

  return (
    <div className="space-y-3">
      {maxAxes !== undefined && (
        <p className="text-sm text-muted-foreground">
          {t('products.detail.variants.assign.limit', { max: maxAxes })}
        </p>
      )}
      {axes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('products.detail.variants.assign.empty')}
        </p>
      ) : (
        axes.map((axis) => (
          <div key={axis.id} className="flex items-center gap-3">
            <Checkbox
              id={`axis-${axis.id}`}
              checked={value.includes(axis.id)}
              disabled={isFull && !value.includes(axis.id)}
              onCheckedChange={(checked) => toggle(axis.id, checked === true)}
            />
            <Label htmlFor={`axis-${axis.id}`} className="font-normal">
              {axis.name}
              <span className="text-muted-foreground ml-2">({axis.code})</span>
            </Label>
          </div>
        ))
      )}

      <Button type="button" variant="ghost" size="sm" onClick={() => setIsCreateOpen(true)}>
        <Plus className="size-4" />
        {t('variantAxes.actions.create')}
      </Button>

      {/* React bubbles submit through the portal, so without this the modal would also submit an outer product form */}
      <div onSubmit={(event) => event.stopPropagation()}>
        <VariantAxisFormModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onSuccess={() => mutate()}
        />
      </div>
    </div>
  )
}
